import React from 'react';
import { Drawer, Table, Avatar, Typography, Empty, Icon, Tag } from 'antd';
import styled, { keyframes } from 'styled-components';
import moment from 'moment';
import { limitCharacterLength } from '~/utils/formatters';

const { Title, Text } = Typography;

// Skeleton loading animation
var shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

var SkeletonBox = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

var SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
`;

var SkeletonAvatar = styled(SkeletonBox)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 12px;
`;

var SkeletonText = styled(SkeletonBox)`
  height: ${function(props) { return props.height || '14px'; }};
  width: ${function(props) { return props.width || '100%'; }};
`;

var SkeletonTag = styled(SkeletonBox)`
  height: 22px;
  width: 80px;
  border-radius: 4px;
`;

var SkeletonContainer = styled.div`
  padding: 0;
`;

var SkeletonHeader = styled.div`
  display: flex;
  padding: 12px 0;
  border-bottom: 2px solid #f0f0f0;
  margin-bottom: 4px;
`;

var SkeletonHeaderCell = styled(SkeletonBox)`
  height: 14px;
  width: ${function(props) { return props.width || '80px'; }};
`;

// Skeleton loading component for the drawer
var DrawerSkeleton = function() {
  var rows = [0, 1, 2, 3, 4];

  return (
    <SkeletonContainer>
      <SkeletonHeader>
        <div style={{ width: '50%', paddingRight: '16px' }}>
          <SkeletonHeaderCell width="60px" />
        </div>
        <div style={{ width: '25%', paddingRight: '16px' }}>
          <SkeletonHeaderCell width="90px" />
        </div>
        <div style={{ width: '25%' }}>
          <SkeletonHeaderCell width="70px" />
        </div>
      </SkeletonHeader>
      {rows.map(function(index) {
        return (
          <SkeletonRow key={index}>
            <div style={{ width: '50%', display: 'flex', alignItems: 'center', paddingRight: '16px' }}>
              <SkeletonAvatar />
              <SkeletonText width={index % 2 === 0 ? '140px' : '100px'} />
            </div>
            <div style={{ width: '25%', paddingRight: '16px' }}>
              <SkeletonTag />
            </div>
            <div style={{ width: '25%' }}>
              <SkeletonText width="70px" />
            </div>
          </SkeletonRow>
        );
      })}
    </SkeletonContainer>
  );
};

// Helper function to get cadence step status for a lead
const getCadenceStepStatus = (lead, summaryData) => {
  if (!lead.additional_data || !lead.additional_data.cadences) {
    return { status: 'no-cadence', label: 'Sem cadência', color: 'default' };
  }

  // If this is a step column, find the specific step status
  if (summaryData.columnId && summaryData.columnId.toString().startsWith('step_')) {
    const stepUid = summaryData.columnId.replace('step_', '');
    
    for (const cadence of lead.additional_data.cadences) {
      if (cadence.data && Array.isArray(cadence.data)) {
        const task = cadence.data.find(t => t.uid === stepUid);
        if (task) {
          const statusMap = {
            'pending': { label: 'Pendente', color: 'orange' },
            'done': { label: 'Concluído', color: 'green' },
            'canceled': { label: 'Sem sucesso', color: 'red' }
          };
          return statusMap[task.status] || { label: 'Desconhecido', color: 'default' };
        }
      }
    }
  }

  // For other columns, show the overall cadence status
  for (const cadence of lead.additional_data.cadences) {
    if (cadence.data && Array.isArray(cadence.data)) {
      const pendingTasks = cadence.data.filter(task => task.status === 'pending');
      const doneTasks = cadence.data.filter(task => task.status === 'done');
      const canceledTasks = cadence.data.filter(task => task.status === 'canceled');
      
      if (canceledTasks.length > 0) {
        return { label: 'Sem sucesso', color: 'red' };
      } else if (pendingTasks.length > 0) {
        return { label: 'Em andamento', color: 'blue' };
      } else if (doneTasks.length > 0) {
        return { label: 'Concluído', color: 'green' };
      }
    }
  }

  return { status: 'no-tasks', label: 'Sem tarefas', color: 'default' };
};

// Helper function to get the current column of a lead
const getLeadCurrentColumn = (lead, allColumns) => {
  if (!allColumns || !Array.isArray(allColumns)) {
    return { name: 'Desconhecido', color: 'default' };
  }

  // Find the column where this lead currently is
  const currentColumn = allColumns.find(col => 
    col.data && Array.isArray(col.data) && 
    col.data.some(l => l.id === lead.id)
  );

  if (currentColumn) {
    return {
      name: currentColumn.name,
      color: currentColumn.style ? currentColumn.style.color : '#666666'
    };
  }

  // If not found in any column, check the lead's column_id
  if (lead.column_id) {
    const columnById = allColumns.find(col => col.id === lead.column_id);
    if (columnById) {
      return {
        name: columnById.name,
        color: columnById.style ? columnById.style.color : '#666666'
      };
    }
  }

  return { name: 'Não definido', color: 'default' };
};

// Helper function to get the action date for a lead's cadence step
const getCadenceStepActionDate = (lead, summaryData) => {
  if (!lead.additional_data || !lead.additional_data.cadences) {
    return null;
  }

  // If this is a step column, find the specific step action date
  if (summaryData.columnId && summaryData.columnId.toString().startsWith('step_')) {
    const stepUid = summaryData.columnId.replace('step_', '');
    
    for (const cadence of lead.additional_data.cadences) {
      if (cadence.data && Array.isArray(cadence.data)) {
        const task = cadence.data.find(t => t.uid === stepUid);
        if (task) {
          // Return the appropriate timestamp based on status
          if (task.status === 'done' && task.done_at) {
            return task.done_at;
          } else if (task.status === 'canceled' && task.done_at) {
            return task.done_at;
          } else if (task.status === 'pending') {
            // For pending tasks, show the scheduled date
            return task.date;
          }
        }
      }
    }
  }

  // For other columns, return the lead creation date as fallback
  return lead.created_at;
};

const SummaryDrawer = ({ 
  isVisible, 
  onClose, 
  summaryData, 
  leads, 
  isLoading = false,
  allColumns = []
}) => {

  // Determine if this is a step column
  const isStepColumn = summaryData && summaryData.columnId && summaryData.columnId.toString().startsWith('step_');
  
  // Determine if this is the Inicio column (id: 1)
  const isInicioColumn = summaryData && summaryData.columnId === 1;
  
  // Determine if this is the Leads column (id: 2)
  const isLeadsColumn = summaryData && summaryData.columnId === 2;

  const columns = [
    {
      title: 'Lead',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar icon={<Icon type="user" />} style={{ marginRight: '8px' }} />
          <Text strong>{limitCharacterLength(name, 40)}</Text>
        </div>
      ),
      width: isStepColumn || isInicioColumn || isLeadsColumn ? '50%' : '100%',
    },
    // Show current column for Inicio column
    ...(isInicioColumn ? [{
      title: 'Coluna Atual',
      key: 'currentColumn',
      render: (_, record) => {
        const columnInfo = getLeadCurrentColumn(record, allColumns);
        return (
          <Tag color={columnInfo.color}>
            {columnInfo.name}
          </Tag>
        );
      },
      width: '25%',
    }] : []),
    // Show current column for Leads column
    ...(isLeadsColumn ? [{
      title: 'Coluna Atual',
      key: 'currentColumn',
      render: (_, record) => {
        const columnInfo = getLeadCurrentColumn(record, allColumns);
        return (
          <Tag color={columnInfo.color}>
            {columnInfo.name}
          </Tag>
        );
      },
      width: '25%',
    }] : []),
    // Only show Status da Etapa for step columns
    ...(isStepColumn ? [{
      title: 'Status da Etapa',
      key: 'cadenceStatus',
      render: (_, record) => {
        const statusInfo = getCadenceStepStatus(record, summaryData);
        return (
          <Tag color={statusInfo.color}>
            {statusInfo.label}
          </Tag>
        );
      },
      width: '25%',
    }] : []),
    // Show Cadastro for Inicio column
    ...(isInicioColumn ? [{
      title: 'Cadastro',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => {
        const momentDate = moment(date);
        const relativeTime = momentDate.fromNow();
        const fullDate = momentDate.format('DD/MM/YYYY HH:mm');
        
        return (
          <Text type="secondary" title={fullDate}>
            {relativeTime}
          </Text>
        );
      },
      width: '25%',
    }] : []),
    // Show Cadastro for Leads column
    ...(isLeadsColumn ? [{
      title: 'Cadastro',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => {
        const momentDate = moment(date);
        const relativeTime = momentDate.fromNow();
        const fullDate = momentDate.format('DD/MM/YYYY HH:mm');
        
        return (
          <Text type="secondary" title={fullDate}>
            {relativeTime}
          </Text>
        );
      },
      width: '25%',
    }] : []),
    // Only show Data da ação for step columns
    ...(isStepColumn ? [{
      title: 'Data da ação',
      key: 'actionDate',
      render: (_, record) => {
        const actionDate = getCadenceStepActionDate(record, summaryData);
        if (!actionDate) return null;
        
        const formattedDate = moment(actionDate, 'DD/MM/YYYY HH:mm:ss');
        if (!formattedDate.isValid()) return null;
        
        const relativeTime = formattedDate.fromNow();
        const fullDate = formattedDate.format('DD/MM/YYYY HH:mm');
        
        return (
          <Text type="secondary" title={fullDate}>
            {relativeTime}
          </Text>
        );
      },
      width: '25%',
    }] : []),
  ];

  const getDrawerTitle = () => {
    if (!summaryData) return 'Detalhes';
    
    const actualCount = leads ? leads.length : 0;
    return `${summaryData.title}`;
  };

  const getDrawerSubtitle = () => {
    if (!summaryData) return '';
    
    // Show done and undone counts only for step columns
    if (isStepColumn && summaryData.done !== undefined && summaryData.undone !== undefined) {
      return `${summaryData.done} concluídos, ${summaryData.undone} cancelados`;
    }
    
    return summaryData.tooltip || `Leads relacionados a ${summaryData.title}`;
  };

  return (
    <Drawer
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {getDrawerTitle()}
          </Title>
          {getDrawerSubtitle() && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {getDrawerSubtitle()}
            </Text>
          )}
        </div>
      }
      placement="right"
      onClose={onClose}
      visible={isVisible}
      width={600}
      bodyStyle={{ padding: '16px' }}
    >
      {isLoading ? (
        <DrawerSkeleton />
      ) : leads && leads.length > 0 ? (
        <Table
          columns={columns}
          dataSource={leads}
          rowKey="id"
          pagination={false}
          size="small"
          locale={{
            emptyText: <Empty description="Nenhum lead encontrado" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          }}
        />
      ) : (
        <Empty
          description="Nenhum lead encontrado"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
    </Drawer>
  );
};

export default SummaryDrawer; 